import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../CSS/Global.css';
import './CustomerStyle.css';
import Swal from 'sweetalert2';
import { getDecryptedUserFromCookie } from '../utils/getCookie';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Mail, Phone, Search, X } from 'lucide-react';
import { useSearch } from '../../SearchContext';
import { useFilter } from '../../FilterContext';
import FilterDrawer from '../FilterDrawer';
import Loader from '../Loader';
import { decryptToken } from '../utils/getToken';

const Customer = () => {
  const navigate = useNavigate();
  const { globalSearch } = useSearch();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isImportOpen, setImportOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const url = API_BASE_URL
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const { entriesPerPage, sortOrder, showDrawer, setShowDrawer, customDate } = useFilter();
  const [load, setLoad] = useState(false)
  const [showSearchBar, setShowSearchBar] = useState(false);
  const token = decryptToken("accessToken") || ""

  const fetchCustomers = async () => {
    try {
      setLoad(true); // Start loading
      const response = await fetch(`${url}/api/customer/all/${isLoggedIn.main_id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      const data = await response.json();
      const parsedCustomers = data.map((customer) => ({
        ...customer,
        billingAddress: customer.billingAddress ? JSON.parse(customer.billingAddress) : {},
        shippingAddress: customer.shippingAddress ? JSON.parse(customer.shippingAddress) : {},
        contact: customer.contact ? JSON.parse(customer.contact) : [],
      }));
      setCustomers(parsedCustomers);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoad(false); // Stop loading after everything is done
    }
  };


  useEffect(() => {
    fetchCustomers();
  }, []);

  // 1. Filter by date
  let processedCustomers = [...customers];

  if (customDate.from || customDate.to) {
    processedCustomers = processedCustomers.filter((customer) => {
      const createdAt = new Date(customer.createdAt);
      const from = customDate.from ? new Date(customDate.from) : null;
      const to = customDate.to ? new Date(customDate.to) : null;

      return (!from || createdAt >= from) && (!to || createdAt <= to);
    });
  }

  // 2. Sort by createdAt
  processedCustomers.sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const searchText = showSearchBar ? searchQuery : globalSearch;

  const filteredCustomers = processedCustomers.filter((customer) =>
    (customer.customerName || '').toLowerCase().includes(searchText.toLowerCase()) ||
    (customer.companyName || '').toLowerCase().includes(searchText.toLowerCase()) ||
    (customer.email || '').toLowerCase().includes(searchText.toLowerCase()) ||
    (customer.phone1 || '').toLowerCase().includes(searchText.toLowerCase()) ||
    (customer.gstNumber || '').toLowerCase().includes(searchText.toLowerCase()) ||
    (customer.remarks || '').toLowerCase().includes(searchText.toLowerCase())
  );


  const indexOfLastCustomer = currentPage * entriesPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - entriesPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);
  const totalPages = Math.ceil(filteredCustomers.length / entriesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const handleSearch = (event) => setSearchQuery(event.target.value);
  const toggleDropdown = () => setDropdownOpen(!isDropdownOpen);
  const toggleImport = () => setImportOpen(!isImportOpen);
  const handleRefresh = () => {
    setLoading(true);
    fetchCustomers();
    setTimeout(() => setLoading(false), 1000);
  };

  const handleDelete = async (customerId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This customer will be permanently deleted!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(`${url}/api/customer/${customerId}`, {
            method: 'DELETE',
          });

          if (!response.ok) {
            const err = await response.json();
            throw new Error(err?.message || 'Failed to delete');
          }

          Swal.fire('Deleted!', 'Customer has been deleted.', 'success');
          setCustomers(customers.filter(c => c.id !== customerId));
        } catch (error) {
          console.error('Delete error:', error.message);
          Swal.fire('Error!', `Failed to delete customer. ${error.message}`, 'error');
        }
      }
    });
  };


  const handleEdit = (customer) => {
    navigate('/edit-customer', { state: customer });

  };

  const SkeletonLoader = () => (
    <div>
      {Array(10).fill('').map((_, index) => (
        <div className="skeleton-row" key={index}>
          {[...Array(5)].map((_, i) => <div className="skeleton" key={i}></div>)}
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <Loader />
    );
  }
  if (load) {
    return (
      <Loader />
    );
  }

  return (
    <>
      <div className="customers-header">
        <div className="customer-dropdown">
          <p className="dashboard-title" onClick={toggleDropdown}>
            All Customers
          </p>
        </div>

        <div className="customer-warning-new-hamburger-help">
          <div
            className='text-black size-8 rounded-full flex justify-center items-center bg-gray-100 hover:bg-gray-200 cursor-pointer'
            onClick={() => setShowSearchBar(!showSearchBar)}
          >
            <Search size={18} />
          </div>
          <div className="filter-controls">
            <button onClick={() => setShowDrawer(true)} className="more-filters-btn">
              <i className="fa-solid fa-sliders"></i> Filters
            </button>
          </div>
          <div className="import-hamburger" onClick={toggleImport}>
            <i className="fa-solid fa-ellipsis-vertical"></i>
          </div>
          {isImportOpen && (
            <div className="import-ul">
              <ul>
                <ul>
                  <li><Link to='/customer-import'><i className="fa-solid fa-file-arrow-down"></i> Import Customers</Link></li>
                </ul>
                <ul>
                  <li><Link to='/export-customer'><i className="fa-solid fa-file-arrow-up"></i> Export Customers</Link></li>
                </ul>
                <ul>
                  <li onClick={handleRefresh}><i className="fa-solid fa-rotate"></i> Refresh List</li>
                </ul>
                <ul>
                  <li>
                    <a
                      href="/sample_customer.xlsx"
                      download
                      style={{ textDecoration: "none" }}
                    >
                      <i className="fa-solid fa-file-arrow-down"></i> Download Sample
                    </a>

                  </li>
                </ul>
              </ul>
            </div>
          )}
        </div>
      </div>

      {showSearchBar && (
        <div className="w-full my-3">
          <div className="relative w-full">
            <input
              type="text"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              className="w-full border border-gray-300 rounded-full px-4 py-2 pr-10 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              onClick={() => {
                setShowSearchBar(false);
                setSearchQuery('');
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              <X size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      )}

      <div className='mt-5'>
        {
        currentCustomers.length > 0 ? (
          currentCustomers.map((customer, index) => (
            <div
              key={customer.id || index}
              onClick={() => navigate(`/customer-detail/${customer.id}`)}
              className="p-3 mb-4 border border-blue-100 rounded-lg shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer bg-white"
            >
              {customer.customerType && (
                <div className="text-xs tracking-wide text-blue-600 bg-blue-50 rounded-full px-2 py-1  font-medium mb-1 inline-block">
                  {customer.customerType}
                </div>
              )}

              {customer.customerName && (
                <h2 className="text-base font-semibold text-gray-800 mb-2">
                  {customer.customerName} - {customer.cust_ID}
                </h2>
              )}

              {customer.phone1 && (
                <div className="flex items-center text-sm text-gray-700 mb-1">
                  <Phone className="w-4 h-4 text-blue-500 mr-2" strokeWidth={2} />
                  <span>{customer.phone1}</span>
                </div>
              )}

              {customer.email && (
                <div className="flex items-center text-sm text-gray-700">
                  <Mail className="w-4 h-4 text-blue-500 mr-2" strokeWidth={2} />
                  <span>{customer.email}</span>
                </div>
              )}
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500 text-sm my-12">No customers found.</p>
        )
      }

      </div>

      {totalPages > 1 && (

        <div className="pagination">
          {/* First Page */}
          <button
            onClick={() => paginate(1)}
            disabled={currentPage === 1}
            className="pagination-chev-btn"
          >
            <ChevronsLeft size={18} />
          </button>

          {/* Previous Page */}
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-chev-btn"
          >
            <ChevronLeft size={18} />
          </button>

          {/* Current Page Number Only */}
          <button className="pagination-btn active">
            {currentPage}
          </button>

          {/* Next Page */}
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-chev-btn"
          >
            <ChevronRight size={18} />
          </button>

          {/* Last Page */}
          <button
            onClick={() => paginate(totalPages)}
            disabled={currentPage === totalPages}
            className="pagination-chev-btn"
          >
            <ChevronsRight size={18} />
          </button>
        </div>
      )}

      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <button
          onClick={() => navigate('/add-customer')}
          className="cssbuttons-io-button">
          <span>Add Customer</span>
        </button>
      </div>
      <FilterDrawer />
    </>
  );
};

export default Customer;
